import React, { Suspense, lazy, useEffect, useRef, useState } from 'react';
import {
  BrowserRouter as Router,
  Route,
  Routes,
  useLocation,
} from 'react-router-dom';
import './App.scss';
import { AuthProvider } from './context/AuthContext';
import ProtectedRoute from './components/ProtectedRoute/ProtectedRoute';
import Header from './components/Header/Header';
import { usePrefersReducedMotion } from './hooks/usePrefersReducedMotion';

const Home = lazy(() => import('./views/Home/Home'));
const Address = lazy(() => import('./views/Address/Address'));
const Bond = lazy(() => import('./views/Bond/Bond'));
const Period = lazy(() => import('./views/Period/Period'));
const Project = lazy(() => import('./views/Project/Project'));
const Round = lazy(() => import('./views/Round/Round'));
const FirstBuys = lazy(() => import('./views/FirstBuys/FirstBuys'));

/**
 * Shown while a route chunk is loading. Keeps layout stable and avoids a blank flash.
 */
const RouteFallback: React.FC = () => (
  <div
    className="bd-view-route-fallback d-flex flex-column justify-content-center align-items-center py-5"
    role="status"
    aria-live="polite"
  >
    <p className="bd-view-loading-compact mb-0" aria-hidden>
      Loading this page…
    </p>
    <i
      className="fa-solid fa-circle-notch fa-spin fa-2x bd-view-loading-icon"
      aria-hidden
    />
    <span className="visually-hidden">Loading page…</span>
  </div>
);

type RouteMotionClass = 'bd-route-enter' | 'bd-route-exit' | '';

const routeConfig = (
  <>
    {/* Protected routes - require authentication */}
    <Route
      path="/"
      element={
        <ProtectedRoute>
          <Home />
        </ProtectedRoute>
      }
    />
    <Route
      path="/period/:period"
      element={
        <ProtectedRoute>
          <Period />
        </ProtectedRoute>
      }
    />
    <Route
      path="/first-buys"
      element={
        <ProtectedRoute>
          <FirstBuys />
        </ProtectedRoute>
      }
    />

    {/* Public routes - no authentication required */}
    <Route path="/bond/:address" element={<Bond />} />
    <Route path="/address/:address" element={<Address />} />
    <Route path="/project/:address" element={<Project />} />
    <Route path="/round/:slug" element={<Round />} />
  </>
);

/**
 * Route transitions: exit previous view (faster), then enter the next (bolder fade/slide/scale).
 * Uses `Routes location` so the outgoing page stays mounted until the exit animation finishes.
 */
const AppRoutes: React.FC = () => {
  const location = useLocation();
  const locationRef = useRef(location);
  locationRef.current = location;

  const prefersReducedMotion = usePrefersReducedMotion();
  const [displayLocation, setDisplayLocation] = useState(location);
  const [motionClass, setMotionClass] = useState<RouteMotionClass>(() =>
    prefersReducedMotion ? '' : 'bd-route-enter',
  );
  const motionClassRef = useRef(motionClass);
  motionClassRef.current = motionClass;
  const isFirstNavigation = useRef(true);

  useEffect(() => {
    if (prefersReducedMotion) {
      setDisplayLocation(location);
      setMotionClass('');
      return;
    }

    if (isFirstNavigation.current) {
      isFirstNavigation.current = false;
      setMotionClass('bd-route-enter');
      return;
    }

    if (location.pathname === displayLocation.pathname) {
      return;
    }

    setMotionClass('bd-route-exit');
  }, [location, displayLocation.pathname, prefersReducedMotion]);

  const onRouteMotionEnd = (event: React.AnimationEvent<HTMLDivElement>) => {
    if (event.target !== event.currentTarget) {
      return;
    }
    if (motionClassRef.current !== 'bd-route-exit') {
      return;
    }
    setDisplayLocation(locationRef.current);
    setMotionClass('bd-route-enter');
  };

  const routeSurfaceClass = ['bd-route-surface', motionClass]
    .filter(Boolean)
    .join(' ');

  return (
    <main
      className="bd-shell-main"
      aria-busy={motionClass === 'bd-route-exit'}
    >
      <div
        className={routeSurfaceClass}
        onAnimationEnd={onRouteMotionEnd}
      >
        <Routes location={displayLocation}>{routeConfig}</Routes>
      </div>
    </main>
  );
};

const App: React.FC = () => {
  return (
    <AuthProvider>
      <Router
        future={{
          v7_startTransition: true,
          v7_relativeSplatPath: true,
        }}
      >
        <div className="container app-shell mt-3">
          <Header />
          <Suspense fallback={<RouteFallback />}>
            <AppRoutes />
          </Suspense>
        </div>
      </Router>
    </AuthProvider>
  );
};

export default App;
